// apps/client/src/world/terrain/ground.ts (light and look, 2026-10-16) — the ground map: one small RGBA texture over the island that the
// grass tops, the ramps and the sand read in the world shader (engine/shader/worldPatch.ts, materials with userData.ground):
//   R  soft, cool shade under tree canopies and round the foot of buildings and rocks (cheap ambient occlusion, all qualities)
//   G  worn earth round the stepping stones and the plaza's slab (the shapes drawn, not the path tiles)
//   B  broad meadow patches, dry (above 128) or lush (below)
//   A  the warm lamp pool at every lived-in door, lit after dark by the preset's `lamps`
// G and B depend only on the map, the town seed and the decor's stones (groundBase, once per town); R and A on the objects (paintGround, again whenever
// one is added or removed). A second, one-channel map holds the sun's soft shadows for low quality, which draws no shadow maps
// (paintSunShadows, again when the sun has moved). Pure apart from GroundLayer, so Node tests read the bytes. Values in
// tuning.light.ground.
import { tuning } from '@whiskerton/content';
import { rngFrom, type MapData } from '@whiskerton/shared';
import * as THREE from 'three';

const G = tuning.light.ground;
/** The texture's footprint: its first texel's corner (world x, z), its size in texels, and texels per tile. */
export interface GroundShape { x0: number; z0: number; width: number; height: number; perTile: number }
/** What the objects leave on the ground: canopies (centre, radius, height above the ground), rocks (centre, radius), building
 *  footprints (world rect, height) and lit doors. */
export interface GroundMarks {
  trees: readonly { x: number; z: number; r: number; h: number }[]; stones: readonly { x: number; z: number; r: number }[];
  blocks: readonly { x0: number; z0: number; x1: number; z1: number; h: number }[]; lamps: readonly { x: number; z: number }[];
}
export interface GroundBase { path: Uint8Array; patch: Uint8Array }
/** The worn earth's shapes, as world/decor.ts draws them: each stepping stone (centre and radius) and the plaza's round slab (centre and
 *  radii), or none. */
export interface GroundWorn {
  stones: readonly { x: number; z: number; r: number }[]; plaza: { x: number; z: number; rx: number; rz: number } | null;
}

export function groundShape(map: Pick<MapData, 'w' | 'h'>): GroundShape {
  return { x0: -G.margin, z0: -G.margin, width: (map.w + 2 * G.margin) * G.perTile, height: (map.h + 2 * G.margin) * G.perTile, perTile: G.perTile };
}
const smooth = (t: number) => { const x = Math.min(1, Math.max(0, t)); return x * x * (3 - 2 * x); };

/** One octave of periodic-free value noise over world (x, z), `cell` tiles a lattice cell, in −1…1. */
function valueNoise(seed: number, key: string, cell: number, w: number, h: number): (x: number, z: number) => number {
  const nx = Math.ceil(w / cell) + 3, nz = Math.ceil(h / cell) + 3, rng = rngFrom('ground', key, seed), lattice = new Float32Array(nx * nz);
  for (let i = 0; i < lattice.length; i++) lattice[i] = rng.next() * 2 - 1;
  const v = (i: number, j: number) => lattice[Math.min(nz - 1, Math.max(0, j)) * nx + Math.min(nx - 1, Math.max(0, i))]!;
  return (x, z) => {
    const fx = (x + G.margin) / cell + 1, fz = (z + G.margin) / cell + 1, i = Math.floor(fx), j = Math.floor(fz), tx = smooth(fx - i), tz = smooth(fz - j);
    const a = v(i, j), b = v(i + 1, j), c = v(i, j + 1), d = v(i + 1, j + 1);
    return a + (b - a) * tx + (c - a) * tz + (a - b - c + d) * tx * tz;
  };
}
/** Separable box blur of a 0…1 mask, `r` texels each side. */
function blur(src: Float32Array, w: number, h: number, r: number): Float32Array {
  const tmp = new Float32Array(src.length), out = new Float32Array(src.length), n = 2 * r + 1;
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let s = 0;
    for (let k = -r; k <= r; k++) s += src[y * w + Math.min(w - 1, Math.max(0, x + k))]!;
    tmp[y * w + x] = s / n;
  }
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let s = 0;
    for (let k = -r; k <= r; k++) s += tmp[Math.min(h - 1, Math.max(0, y + k)) * w + x]!;
    out[y * w + x] = s / n;
  }
  return out;
}

/** The map's own channels: worn earth round the shapes actually drawn (each stepping stone, the plaza's round slab: never the path
 *  tiles themselves, whose straight edges and square corners showed as a pale band) and the meadow patches (from the town seed). The
 *  wear is full under a shape and eases out over `path.soft` tiles past its rim, then a one-texel blur takes off the raster's steps. */
export function groundBase(map: MapData, seed: number, s: GroundShape, worn: GroundWorn): GroundBase {
  const { width: w, height: h, perTile: k } = s, wear = new Float32Array(w * h), P = G.path;
  const put = (n: number, v: number) => { if (v > wear[n]!) wear[n] = v; };
  for (const t of worn.stones) {
    const R = t.r + P.soft;
    texels(s, t.x - R, t.z - R, t.x + R, t.z + R, (n, x, z) => put(n, 1 - smooth((Math.hypot(x - t.x, z - t.z) - t.r) / P.soft)));
  }
  const p = worn.plaza;
  if (p) {
    const Rx = p.rx + P.soft, Rz = p.rz + P.soft;
    texels(s, p.x - Rx, p.z - Rz, p.x + Rx, p.z + Rz, (n, x, z) => {
      const q = Math.hypot((x - p.x) / p.rx, (z - p.z) / p.rz), out = q > 0 ? Math.hypot(x - p.x, z - p.z) * (1 - 1 / q) : 0;   // past the rim
      put(n, 1 - smooth(out / P.soft));
    });
  }
  const soft = blur(wear, w, h, 1);
  const coarse = valueNoise(seed, 'patch', G.patch.scale, map.w + 2 * G.margin, map.h + 2 * G.margin);
  const fine = valueNoise(seed, 'patch-fine', G.patch.scale / 2.3, map.w + 2 * G.margin, map.h + 2 * G.margin);
  const path = new Uint8Array(w * h), patch = new Uint8Array(w * h);
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) {
    const x = s.x0 + (i + 0.5) / k, z = s.z0 + (j + 0.5) / k, n = j * w + i;
    path[n] = Math.round(soft[n]! * P.amount * 255);
    const v = Math.max(-1, Math.min(1, (coarse(x, z) * 0.65 + fine(x, z) * 0.35) * 1.7));
    patch[n] = Math.round(128 + v * 127 * G.patch.amount);
  }
  return { path, patch };
}

/** Packs the base with the objects' shade and lamp pools into RGBA bytes (into `out` when given). */
export function paintGround(s: GroundShape, base: GroundBase, marks: GroundMarks,
  out: Uint8Array<ArrayBuffer> = new Uint8Array(s.width * s.height * 4)): Uint8Array<ArrayBuffer> {
  const { width: w, height: h, perTile: k } = s, shade = new Float32Array(w * h), lamp = new Float32Array(w * h);
  /** Visits every texel whose centre lies in the world box, with its centre. */
  const each = (x0: number, z0: number, x1: number, z1: number, fn: (n: number, x: number, z: number) => void) => {
    const i0 = Math.max(0, Math.floor((x0 - s.x0) * k)), i1 = Math.min(w - 1, Math.ceil((x1 - s.x0) * k));
    const j0 = Math.max(0, Math.floor((z0 - s.z0) * k)), j1 = Math.min(h - 1, Math.ceil((z1 - s.z0) * k));
    for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) fn(j * w + i, s.x0 + (i + 0.5) / k, s.z0 + (j + 0.5) / k);
  };
  const add = (n: number, v: number) => { if (v > 0) shade[n] = 1 - (1 - shade[n]!) * (1 - v); };
  const disc = (list: GroundMarks['stones'], reach: number, strength: number) => {
    for (const t of list) {
      const R = t.r * reach;
      each(t.x - R, t.z - R, t.x + R, t.z + R, (n, x, z) => {
        const d = Math.hypot(x - t.x, z - t.z) / R;
        if (d < 1) add(n, strength * (1 - d * d) ** 2);
      });
    }
  };
  disc(marks.trees, G.shade.treeReach, G.shade.tree);
  disc(marks.stones, G.shade.stoneReach, G.shade.stone);
  const reach = G.shade.buildingReach;
  for (const b of marks.blocks) each(b.x0 - reach, b.z0 - reach, b.x1 + reach, b.z1 + reach, (n, x, z) => {
    const dx = Math.max(b.x0 - x, 0, x - b.x1), dz = Math.max(b.z0 - z, 0, z - b.z1), d = Math.hypot(dx, dz);
    if (d < reach) add(n, G.shade.building * (1 - smooth(d / reach)));
  });
  const R = G.lamp.radius;
  for (const l of marks.lamps) each(l.x - R, l.z - R, l.x + R, l.z + R, (n, x, z) => {
    const d = Math.hypot(x - l.x, z - l.z) / R;
    if (d < 1) lamp[n] = Math.max(lamp[n]!, (1 - d * d) ** 2);
  });
  for (let n = 0; n < w * h; n++) {
    out[n * 4] = Math.round(shade[n]! * 255); out[n * 4 + 1] = base.path[n]!; out[n * 4 + 2] = base.patch[n]!; out[n * 4 + 3] = Math.round(lamp[n]! * 255);
  }
  return out;
}

const SUN = G.sun;
/** Visits every texel whose centre lies in the world box. */
function texels(s: GroundShape, x0: number, z0: number, x1: number, z1: number, fn: (n: number, x: number, z: number) => void): void {
  const { width: w, height: h, perTile: k } = s;
  const i0 = Math.max(0, Math.floor((x0 - s.x0) * k)), i1 = Math.min(w - 1, Math.ceil((x1 - s.x0) * k));
  const j0 = Math.max(0, Math.floor((z0 - s.z0) * k)), j1 = Math.min(h - 1, Math.ceil((z1 - s.z0) * k));
  for (let j = j0; j <= j1; j++) for (let i = i0; i <= i1; i++) fn(j * w + i, s.x0 + (i + 0.5) / k, s.z0 + (j + 0.5) / k);
}
/** The sun's soft shadows on the ground (one byte a texel, into `out` when given), for low quality: each canopy as an ellipse cast
 *  from its centre away from the sun and stretched the lower the sun, each building's footprint swept away by its height. `sun` points
 *  toward the sun; a sun at or below the horizon casts nothing. */
export function paintSunShadows(s: GroundShape, marks: GroundMarks, sun: { x: number; y: number; z: number },
  out: Uint8Array<ArrayBuffer> = new Uint8Array(s.width * s.height)): Uint8Array<ArrayBuffer> {
  out.fill(0);
  if (!(sun.y > 0.02)) return out;
  const flat = Math.hypot(sun.x, sun.z) || 1, dx = -sun.x / flat, dz = -sun.z / flat;   // the way shadows fall
  const run = flat / sun.y, stretch = Math.min(SUN.stretch, 1 / sun.y);                  // ground offset per unit of height; elongation
  const v = new Float32Array(s.width * s.height);
  const put = (n: number, a: number) => { if (a > v[n]!) v[n] = a; };
  for (const t of marks.trees) {
    const off = t.h * SUN.canopyAt * run, cx = t.x + dx * off, cz = t.z + dz * off, a = t.r * stretch, b = t.r, R = Math.max(a, b);
    texels(s, cx - R, cz - R, cx + R, cz + R, (n, x, z) => {
      const u = (x - cx) * dx + (z - cz) * dz, w = (x - cx) * -dz + (z - cz) * dx, q = (u / a) ** 2 + (w / b) ** 2;
      if (q < 1) put(n, SUN.tree * (1 - smooth((q - 0.45) / 0.55)));
    });
  }
  for (const b of marks.blocks) {
    const i = SUN.inset, r = { x0: b.x0 + i, z0: b.z0 + i, x1: Math.max(b.x0 + i, b.x1 - i), z1: Math.max(b.z0 + i, b.z1 - i) };
    const off = b.h * SUN.buildingHeight * run, ox = dx * off, oz = dz * off, steps = Math.max(1, Math.ceil(off * 2)), e = SUN.soft;
    texels(s, Math.min(r.x0, r.x0 + ox) - e, Math.min(r.z0, r.z0 + oz) - e, Math.max(r.x1, r.x1 + ox) + e, Math.max(r.z1, r.z1 + oz) + e, (n, x, z) => {
      let d = Infinity;                                                                   // distance to the footprint swept from 0 to off
      for (let k = 0; k <= steps; k++) {
        const f = k / steps, ax = r.x0 + ox * f, az = r.z0 + oz * f;
        d = Math.min(d, Math.hypot(Math.max(ax - x, 0, x - (ax + r.x1 - r.x0)), Math.max(az - z, 0, z - (az + r.z1 - r.z0))));
      }
      if (d < e) put(n, SUN.building * (1 - smooth(d / e)));
    });
  }
  const fade = smooth((sun.y - 0.02) / Math.max(1e-3, SUN.fadeBelow - 0.02));
  for (let n = 0; n < v.length; n++) out[n] = Math.round(v[n]! * fade * 255);
  return out;
}

/** The ground map of one town as a texture: `xform` = (x0, z0, 1 / width in world units, 1 / height), so uv = ((x, z) − xy) × zw. */
export class GroundLayer {
  readonly texture: THREE.DataTexture;
  /** The sun's soft shadows (low quality), one channel, the same footprint as `texture`. */
  readonly sunTexture: THREE.DataTexture;
  readonly xform: THREE.Vector4;
  private readonly shape: GroundShape;
  private readonly base: GroundBase;
  private readonly data: Uint8Array<ArrayBuffer>;
  private readonly sunData: Uint8Array<ArrayBuffer>;
  private marks: GroundMarks = { trees: [], stones: [], blocks: [], lamps: [] };
  /** The sun the shadows were last painted for, or null when the objects have changed since. */
  private sunAt: THREE.Vector3 | null = null;
  constructor(map: MapData, seed: number, worn: GroundWorn) {
    this.shape = groundShape(map);
    this.base = groundBase(map, seed, this.shape, worn);
    const { width: w, height: h, perTile: k } = this.shape;
    this.data = paintGround(this.shape, this.base, { trees: [], stones: [], blocks: [], lamps: [] });
    this.texture = new THREE.DataTexture(this.data, w, h, THREE.RGBAFormat);
    this.texture.magFilter = this.texture.minFilter = THREE.LinearFilter;   // a smooth map: no mipmaps needed
    this.texture.wrapS = this.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.texture.colorSpace = THREE.NoColorSpace;                           // data, not colour
    this.texture.needsUpdate = true;
    this.xform = new THREE.Vector4(this.shape.x0, this.shape.z0, 1 / (w / k), 1 / (h / k));
    this.sunData = new Uint8Array(w * h);
    this.sunTexture = new THREE.DataTexture(this.sunData, w, h, THREE.RedFormat);
    this.sunTexture.magFilter = this.sunTexture.minFilter = THREE.LinearFilter;
    this.sunTexture.wrapS = this.sunTexture.wrapT = THREE.ClampToEdgeWrapping;
    this.sunTexture.colorSpace = THREE.NoColorSpace;
    this.sunTexture.needsUpdate = true;
  }
  /** Repaints the objects' shade and lamp pools (and, at the next sun(), their sun shadows). */
  mark(marks: GroundMarks): void {
    this.marks = marks;
    paintGround(this.shape, this.base, marks, this.data);
    this.texture.needsUpdate = true;
    this.sunAt = null;
  }
  /** Paints the sun's shadows for `sunDir` (toward the sun) unless they were painted for nearly the same sun since the objects last
   *  changed; true when it repainted. The sun moves slowly (town time is real time), so this is a repaint every few minutes. */
  sun(sunDir: THREE.Vector3): boolean {
    if (this.sunAt && this.sunAt.angleTo(sunDir) < SUN.repaintAngle) return false;
    paintSunShadows(this.shape, this.marks, sunDir, this.sunData);
    this.sunTexture.needsUpdate = true;
    this.sunAt = sunDir.clone();
    return true;
  }
  dispose(): void { this.texture.dispose(); this.sunTexture.dispose(); }
}
